import * as React from 'react';

import { enumTruckSortOption } from '@rematter/frontend-lib-api-client';
import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import { cn, ComboBox } from '@rematter/ui-web';

import { useTrucksQuery } from '~/modules/transportation/hooks/queries/useTrucksQuery';
import useDebouncedValue from '~/shared/hooks/useDebounce';

export type TruckSelectProps = {
  value?: string;
  onChange?: (value: React.Key | null) => void;
  className?: string;
  label?: string;
  placeholder?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  autoFocus?: boolean;
  excludeIds?: string[];
};

export const TruckSelect = ({
  value,
  onChange,
  ...props
}: TruckSelectProps) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
  const t = useTranslations();

  const { data, isLoading } = useTrucksQuery({
    searchTerm: debouncedSearchTerm || undefined,
    sorting: [enumTruckSortOption.LicensePlateAsc],
    limit: 50,
  });

  const comboBoxOptions = React.useMemo(
    () =>
      data?.trucks
        ?.filter((truck) => !props.excludeIds?.includes(truck.id))
        .map((truck) => ({
          id: truck.id,
          label: `${truck.model} (${truck.licensePlate})`,
        })) ?? [],
    [data?.trucks, props.excludeIds],
  );

  const handleSearch = React.useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  return (
    <ComboBox
      {...props}
      autoFocus={props.autoFocus}
      options={comboBoxOptions}
      value={value ? [value] : []}
      onChange={(values) => onChange?.(values?.[0] ?? null)}
      isLoading={isLoading}
      query={searchTerm}
      onChangeQuery={handleSearch}
      placeholder={props.placeholder ?? t.servicesView.form.placeholders.truck}
      className={cn('@TruckSelect', props.className)}
      getSelection={(ids) => {
        const truck = data?.trucks?.find((t) => t.id === ids[0]);
        return truck
          ? [{ label: `${truck.model} (${truck.licensePlate})` }]
          : [];
      }}
    />
  );
};
